import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../UI/card';

const PassageText = ({ passage, colors }) => {
  if (!passage) return null;

  const { title, content, level, quadrant } = passage;

  // Create dynamic styles based on colors and level
  const cardStyle = {
    borderColor: colors.primary === 'emerald' ? '#a7f3d0' : 
                 colors.primary === 'blue' ? '#bfdbfe' :
                 colors.primary === 'purple' ? '#c4b5fd' :
                 colors.primary === 'orange' ? '#fed7aa' :
                 colors.primary === 'red' ? '#fecaca' : '#d1d5db',
    backgroundColor: colors.primary === 'emerald' ? '#f0fdf4' : 
                     colors.primary === 'blue' ? '#f0f9ff' :
                     colors.primary === 'purple' ? '#faf5ff' :
                     colors.primary === 'orange' ? '#fffbeb' :
                     colors.primary === 'red' ? '#fef2f2' : '#f9fafb'
  };

  const headerStyle = {
    background: colors.primary === 'emerald' ? 'linear-gradient(to right, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.1))' : 
                colors.primary === 'blue' ? 'linear-gradient(to right, rgba(59, 130, 246, 0.1), rgba(14, 165, 233, 0.1))' :
                colors.primary === 'purple' ? 'linear-gradient(to right, rgba(139, 92, 246, 0.1), rgba(124, 58, 237, 0.1))' :
                colors.primary === 'orange' ? 'linear-gradient(to right, rgba(249, 115, 22, 0.1), rgba(245, 158, 11, 0.1))' :
                colors.primary === 'red' ? 'linear-gradient(to right, rgba(239, 68, 68, 0.1), rgba(244, 63, 94, 0.1))' : 
                'linear-gradient(to right, rgba(107, 114, 128, 0.1), rgba(75, 85, 99, 0.1))'
  };

  const titleColor = colors.primary === 'emerald' ? 'text-emerald-800' : 
                     colors.primary === 'blue' ? 'text-blue-800' :
                     colors.primary === 'purple' ? 'text-purple-800' :
                     colors.primary === 'orange' ? 'text-orange-800' :
                     colors.primary === 'red' ? 'text-red-800' : 'text-gray-800';

  // Get level badge style
  const getLevelBadgeStyle = (level) => {
    const levelStyles = {
      low: { bg: '#fef3c7', color: '#92400e', text: 'Developing' },
      middle: { bg: '#dbeafe', color: '#1e40af', text: 'Proficient' },
      high: { bg: '#dcfce7', color: '#166534', text: 'Advanced' },
      optimistic: { bg: '#dcfce7', color: '#166534', text: 'Optimistic Style' },
      prideful: { bg: '#fed7aa', color: '#c2410c', text: 'Prideful Style' },
      humble: { bg: '#fef3c7', color: '#92400e', text: 'Humble Style' },
      pessimistic: { bg: '#fecaca', color: '#dc2626', text: 'Pessimistic Style' },
      general: { bg: '#f3f4f6', color: '#374151', text: 'General' }
    };
    return levelStyles[level] || levelStyles.general;
  };

  const levelBadge = getLevelBadgeStyle(level);

  // Clean and format the title (remove emoji and extra text)
  const cleanTitle = title.replace(/^[🟫🟥🟧🟨🟦🔴🟦🟨🔵🟢🎯]\s*/, '').split('(')[0].trim();

  return (
    <Card className="border-2 shadow-lg" style={cardStyle}>
      <CardHeader style={headerStyle}>
        <div className="flex items-center justify-between">
          <CardTitle className={`text-lg font-semibold ${titleColor} flex items-center`}>
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
            {cleanTitle}
          </CardTitle>
          <div 
            className="px-3 py-1 rounded-full text-xs font-medium"
            style={{ backgroundColor: levelBadge.bg, color: levelBadge.color }}
          >
            {levelBadge.text}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-6">
        <div className="prose prose-sm max-w-none">
          <p className="text-gray-700 leading-relaxed text-sm">
            {content}
          </p>
        </div>
        
        {/* Add a subtle footer with passage type info */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center text-xs text-gray-500">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {quadrant ? 'Personalized based on your attributional style' : `Personalized for ${levelBadge.text.toLowerCase()} performance level`}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PassageText;
